import React, { useState, useEffect, useContext } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
// Import Shared Components
import {
  Loader,
  ErrorComponent,
  Table,
  ButtonGroup,
  IconButton,
} from 'shared-components';
// Import Shared Utils
import { get, deleteHTTP } from 'shared-components/src/utils/http';
// Import Global Components
import Card from '../../../../components/Card';
import Heading from '../../../../components/Heading';
import Button from '../../../../components/Button';
import Alert from '../../../../components/Alert';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
// Import Hooks
import useAlert from '../../../../hooks/useAlert';
// Import Local Components
import CreateRecipientAccountModal from '../../components/CreateRecipientAccountModal';
import UpdateRecipientAccountModal from './components/UpdateRecipientAccountModal';
// Import Component Styles
import { Title } from './styles';
import { HeadingWrapper } from '../../styles';

const RecipientAccounts = () => {
  const intl = useIntl();

  // Component states
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useAlert();
  // data states
  const [recipientAccounts, setRecipientAccounts] = useState([]);
  const [updateRecipientAccount, setUpdateRecipientAccount] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deletingRecipient, setDeletingRecipient] = useState(false);

  // Extract theme from Theme Provider
  const theme = useContext(ThemeContext);

  // fetch data upon component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get data for working with external accounts
        const res = await get(
          '/api/v1/cardholder_portal/cardholder/recipient_accounts'
        );

        setRecipientAccounts(res.data);
      } catch (e) {
        setError({ type: 'danger', message: e });
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const deleteRecipient = async id => {
    setDeletingRecipient(true);
    try {
      await deleteHTTP(
        `/api/v1/cardholder_portal/cardholder/recipient_accounts/${id}`
      );
      // Remove the deleted recipient from page state
      setRecipientAccounts(prev =>
        prev.filter(
          recipient => parseInt(recipient.id, 10) !== parseInt(id, 10)
        )
      );

      setAlert({
        type: 'success',
        message: intl.messages['recipient-account-success-delete-alert'],
        messageFor: 'success-delete',
      });

      setShowDeleteModal(false);
      setUpdateRecipientAccount(null);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'delete-account-failure',
      });
    }
    setDeletingRecipient(false);
  };
  const closeConfirmDeleteModal = e => {
    e.preventDefault();
    setShowDeleteModal(false);
    setAlert(false);
  };
  // Define page states
  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  const columns = [
    {
      Header: () => (
        <FormattedMessage
          id="id-table-heading"
          description="Table Header id"
          defaultMessage={intl.messages['id-table-heading']}
        />
      ),
      accessor: 'id',
      maxWidth: 150,
    },
    {
      Header: () => (
        <FormattedMessage
          id="name-table-heading"
          description="Table Header name"
          defaultMessage={intl.messages['name-table-heading']}
        />
      ),
      accessor: 'name',
    },
    {
      Header: () => (
        <FormattedMessage
          id="email-table-heading"
          description="Table Header email"
          defaultMessage={intl.messages['email-table-heading']}
        />
      ),
      accessor: 'email',
    },
    {
      Header: () => (
        <FormattedMessage
          id="create-at-table-heading"
          description="Table Header create at"
          defaultMessage={intl.messages['create-at-table-heading']}
        />
      ),
      accessor: 'created_at',
      Cell: ({ value }) => <FormattedDate value={value} />, //eslint-disable-line
    },
    {
      Header: () => (
        <FormattedMessage
          id="actions-table-heading"
          description="Table Header actions"
          defaultMessage={intl.messages['actions-table-heading']}
        />
      ),
      accessor: 'action',
      innerFocus: true,
      Cell: cellInfo => (
        <ButtonGroup justifyContent="flex-start">
          <IconButton
            iconType="edit"
            onClick={e => {
              e.preventDefault();
              setUpdateRecipientAccount(cellInfo.original);
              setShowUpdateModal(true);
            }}
            color={theme.colors.lightBlue}
          />
          <IconButton
            iconType="trash"
            onClick={e => {
              e.preventDefault();
              setUpdateRecipientAccount(cellInfo.original);
              setShowDeleteModal(true);
            }}
            color={theme.colors.danger}
          />
        </ButtonGroup>
      ),
    },
  ];

  const page = 'recipient-account';

  return (
    <>
      <HeadingWrapper>
        <Heading page={page} heading={intl.messages[`${page}-heading`]} />
        <Button
          buttonText={intl.messages[`${page}-add-new-button`]}
          buttonFor="add-new"
          page={page}
          onClick={e => {
            e.preventDefault();
            setShowCreateModal(true);
          }}
          type="primary"
        />
      </HeadingWrapper>
      <Card>
        <>
          {alert && (
            <Alert
              type={alert.type}
              alert={alert.message}
              alertFor={alert.messageFor}
              page={alert.page ? alert.page : page}
            />
          )}
          <Title
            title={intl.messages[`${page}-list-title`]}
            titleFor="list"
            page={page}
          />
          <Table data={recipientAccounts} columns={columns} />
        </>
      </Card>
      {showCreateModal && (
        <CreateRecipientAccountModal
          toggleModal={setShowCreateModal}
          setSuccess={setAlert}
          recipientAccounts={recipientAccounts}
          setRecipientAccounts={setRecipientAccounts}
        />
      )}
      {showUpdateModal && (
        <UpdateRecipientAccountModal
          toggleModal={setShowUpdateModal}
          setSuccess={setAlert}
          recipientAccount={updateRecipientAccount}
          recipientAccounts={recipientAccounts}
          setRecipientAccount={setUpdateRecipientAccount}
          setRecipientAccounts={setRecipientAccounts}
        />
      )}
      {showDeleteModal && (
        <ConfirmDeleteModal
          toggleModal={closeConfirmDeleteModal}
          loading={deletingRecipient}
          alert={alert}
          deleteCallback={deleteRecipient}
          page={page}
          translations={intl.messages}
          deleteItemValue={updateRecipientAccount.id}
          deleteItemName={updateRecipientAccount.name}
        />
      )}
    </>
  );
};

export default RecipientAccounts;
